import {
  formatKodeVerifikasi,
  isKodeVerifikasi,
  normalisasiKodeVerifikasi,
} from '@satu/shared/verifikasi'
import { type FormEvent, useCallback, useEffect, useId, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { ApiClientError } from '../../api/client'
import { periksaKode } from './api'
import { barisKode, kartuKode } from './format'
import { HasilBaris } from './HasilBaris'
import { KartuPeriksa } from './KartuPeriksa'
import { pesanKegagalanKode } from './pesan'
import { KODE_BENTUK, TEKS } from './strings'
import type { VerifyCodeResult } from './types'

type Keadaan =
  | { jenis: 'diam' }
  | { jenis: 'memeriksa' }
  | { jenis: 'hasil'; hasil: VerifyCodeResult; kode: string; urutan: number }
  /** The public card's TIDAK DITEMUKAN (a 404); in the app a 404 stays a sentence. */
  | { jenis: 'tidakDitemukan'; kode: string; urutan: number }
  | { jenis: 'pesan'; teks: string }

interface KodeRegionProps {
  online: boolean
  /** From the printed QR (`?kode=`), shown in the field. */
  kodeAwal?: string
  /** Check `kodeAwal` on mount without a press (the QR path). */
  otomatis?: boolean
  /** The public page's one button is red; in the app the red one belongs to Cari. */
  tombol?: 'utama' | 'sekunder'
  /** Send the session token (in the app) or not (the public page). */
  auth?: boolean
  /** With `judul` false the region has no heading of its own (the page title is it). */
  judul?: boolean
  /**
   * `baris`: the app's compact hairline row under the form, with the book reference. `kartu`:
   * the public page's card above the form (release 15), the field below it for the next number.
   */
  tampilan?: 'baris' | 'kartu'
  onKode?: (kode: string) => void
  className?: string
}

/**
 * "Periksa nomor referensi": one field, one button, one answer. The number is normalised the
 * way Crockford reads it (case, hyphen, look-alikes) before it leaves the browser; the button
 * label swaps to "Memeriksa…" while pending (no spinner); the answer lands in a polite live
 * region, as a hairline row in the app or a card on the public page, or as one sentence when
 * there is nothing to show. An answer leaves the region once the field holds another number.
 */
export function KodeRegion({
  online,
  kodeAwal = '',
  otomatis = false,
  tombol = 'sekunder',
  auth = true,
  judul = true,
  tampilan = 'baris',
  onKode,
  className,
}: KodeRegionProps) {
  const [kode, setKode] = useState(() => formatKodeVerifikasi(normalisasiKodeVerifikasi(kodeAwal)))
  const [keadaan, setKeadaan] = useState<Keadaan>({ jenis: 'diam' })
  const urutan = useRef(0)
  const otomatisDilakukan = useRef(false)
  const idJudul = useId()
  const idKode = useId()
  const idPetunjuk = useId()
  const pending = keadaan.jenis === 'memeriksa'
  const kartu = tampilan === 'kartu'

  const periksa = useCallback(
    async (masukan: string) => {
      const bersih = normalisasiKodeVerifikasi(masukan)
      if (!isKodeVerifikasi(bersih)) {
        setKeadaan({ jenis: 'pesan', teks: KODE_BENTUK })
        return
      }
      setKode(formatKodeVerifikasi(bersih))
      onKode?.(bersih)
      setKeadaan({ jenis: 'memeriksa' })
      try {
        const hasil = await periksaKode(bersih, { auth })
        urutan.current += 1
        setKeadaan({ jenis: 'hasil', hasil, kode: bersih, urutan: urutan.current })
      } catch (error) {
        if (kartu && error instanceof ApiClientError && error.kind === 'not_found') {
          urutan.current += 1
          setKeadaan({ jenis: 'tidakDitemukan', kode: bersih, urutan: urutan.current })
          return
        }
        setKeadaan({ jenis: 'pesan', teks: pesanKegagalanKode(error) })
      }
    },
    [auth, kartu, onKode],
  )

  // The code the QR brought is checked once, at mount; later checks come from the button.
  useEffect(() => {
    // Once, whatever the URL held: a code typed later must not re-run this on a re-render.
    if (otomatisDilakukan.current) return
    otomatisDilakukan.current = true
    if (!otomatis || kodeAwal.length === 0) return
    if (online) void periksa(kodeAwal)
    else setKeadaan({ jenis: 'pesan', teks: TEKS.offline })
  }, [otomatis, kodeAwal, online, periksa])

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!online || pending) return
    void periksa(kode)
  }

  const form = (
    <form
      onSubmit={onSubmit}
      noValidate
      aria-busy={pending}
      className="flex flex-col gap-4 md:flex-row md:items-start"
    >
      <Field className="md:max-w-[16rem]">
        <FieldLabel htmlFor={idKode}>{TEKS.kode.kodeVerifikasi}</FieldLabel>
        <Input
          id={idKode}
          name="kode"
          value={kode}
          onChange={(event) => {
            const baru = event.target.value
            setKode(baru)
            // An answer stays only beside the code it answers (C13): a different code, typed
            // offline or before Periksa, never sits next to the previous code's answer.
            if (
              (keadaan.jenis === 'hasil' || keadaan.jenis === 'tidakDitemukan') &&
              normalisasiKodeVerifikasi(baru) !== keadaan.kode
            ) {
              setKeadaan({ jenis: 'diam' })
            }
          }}
          aria-describedby={idPetunjuk}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputMode="text"
          maxLength={12}
          className="num"
        />
        <p id={idPetunjuk} className="text-label text-muted">
          {TEKS.kode.petunjuk}
        </p>
      </Field>
      <Button
        type="submit"
        variant={!online ? 'outline' : tombol === 'utama' ? 'default' : 'secondary'}
        disabled={!online}
        aria-disabled={pending || undefined}
        // The label's line (FieldLabel is leading-snug, 17.875 px) plus the field gap (8 px):
        // the button sits level with the input, not the hint.
        className="press md:mt-[calc(var(--text-label)*var(--leading-snug)+--spacing(2))] md:shrink-0"
      >
        {pending ? TEKS.kode.memeriksa : TEKS.kode.tombol}
      </Button>
    </form>
  )

  if (kartu) {
    // The public page: the answer first, where the QR's reader looks, the field below it. The
    // region stays mounted (empty until an answer) and carries no space of its own.
    return (
      <div className={cn('flex flex-col', className)}>
        <div aria-live="polite" data-jawaban="">
          {keadaan.jenis === 'hasil' && (
            <div key={keadaan.urutan} className="mb-6">
              <KartuPeriksa kode={keadaan.kode} kartu={kartuKode(keadaan.hasil)} />
            </div>
          )}
          {keadaan.jenis === 'tidakDitemukan' && (
            <div key={keadaan.urutan} className="mb-6">
              <KartuPeriksa kode={keadaan.kode} kartu={null} />
            </div>
          )}
          {keadaan.jenis === 'pesan' && (
            <p className="measure mb-6 font-prose text-prose text-text">{keadaan.teks}</p>
          )}
        </div>
        {form}
      </div>
    )
  }

  const Wrapper = judul ? 'section' : 'div'
  return (
    <Wrapper
      aria-labelledby={judul ? idJudul : undefined}
      className={cn('flex flex-col gap-4 border border-border p-4 md:p-6', className)}
    >
      {judul && (
        <h2 id={idJudul} className="font-display text-section">
          {TEKS.kode.judul}
        </h2>
      )}
      {form}
      <div aria-live="polite" className="min-h-6">
        {keadaan.jenis === 'hasil' && (
          <ul key={keadaan.urutan} className="border-hairline border-t">
            <HasilBaris baris={barisKode(keadaan.hasil)} index={0} />
          </ul>
        )}
        {keadaan.jenis === 'pesan' && (
          <p className="measure font-prose text-prose text-text">{keadaan.teks}</p>
        )}
      </div>
    </Wrapper>
  )
}
